import {
  css, type SerializedStyles,
} from '@emotion/react';
import {
  forwardRef, type MouseEventHandler, type ReactNode, useCallback, useMemo, useState,
} from 'react';
import { useTheme } from '../../../themes/theme.hooks';
import {
  type FaIcon, FontAwesomeIcon,
} from '../../icon/fontAwesomeIcon.component';
import { usePointerClickEvent } from '../../utils/usePointerClickEvent';
import {
  ButtonStyles,
  getButtonStyle,
} from './button.styles';
import {
  ButtonSize, ButtonStyle, type ButtonStyleProps,
} from './button.types';

const getPrefixIconStyle = css({
  display: 'inline-block',
  marginRight: 10,
});

const suffixIconStyle = css({
  display: 'inline-block',
  marginLeft: 10,
});

export type ButtonProps = Readonly<{
  buttonStyle?: ButtonStyle;
  className?: string;
  'data-testid'?: string;
  isActive?: boolean;
  isDisabled?: boolean;
  isSubmit?: boolean;
  prefixIcon?: FaIcon;
  prefixIconHovered?: FaIcon;
  prefixIconStyle?: SerializedStyles;
  size?: ButtonSize;
  suffixIcon?: FaIcon;

  text: ReactNode | ((style: ButtonStyleProps) => ReactNode);
  onClick?: MouseEventHandler;
}>;

export const ButtonComponent = forwardRef<HTMLButtonElement, ButtonProps>(({
  buttonStyle,
  className,
  isActive,
  isDisabled,
  isSubmit,
  onClick,
  prefixIcon,
  prefixIconHovered,
  prefixIconStyle,
  size,
  suffixIcon,
  text,
  ...restProps
}, ref) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const theme = useTheme();

  const btnStyle = ButtonStyles(theme)[buttonStyle || ButtonStyle.Primary];

  const { onClick: handleClick, onPointerDown, onPointerUp } = usePointerClickEvent(onClick);

  const onMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const ensuredPrefixIcon = useMemo(() => {
    if (isHovered && prefixIconHovered) {
      return prefixIconHovered;
    }
    return prefixIcon;
  }, [isHovered, prefixIcon, prefixIconHovered]);

  return (
    <button
      {...restProps}
      ref={ref}
      css={getButtonStyle({
        btnStyle,
        isDisabled,
        isActive,
        theme,
        size: size || ButtonSize.Medium,
      })}
      onClick={isDisabled ? undefined : handleClick}
      onPointerDown={isDisabled ? undefined : onPointerDown}
      onPointerUp={isDisabled ? undefined : onPointerUp}
      className={className}
      onMouseEnter={isDisabled ? undefined : onMouseEnter}
      onMouseLeave={isDisabled ? undefined : onMouseLeave}
      disabled={isDisabled}
      type={isSubmit ? 'submit' : 'button'}
    >
      {ensuredPrefixIcon && (
        <span css={css(getPrefixIconStyle, prefixIconStyle)}>
          <FontAwesomeIcon icon={ensuredPrefixIcon} />
        </span>
      )}

      {(typeof text === 'function')
        ? text(btnStyle)
        : text}

      {suffixIcon && (
        <span css={suffixIconStyle}>
          <FontAwesomeIcon icon={suffixIcon} />
        </span>
      )}
    </button>
  );
});

ButtonComponent.displayName = 'ButtonComponent';
